import DetectRTC from 'detectrtc';
import { useCallback, useEffect } from 'react';
import { create } from 'zustand';

type PermissionsState = {
  audioPermission: PermissionState | undefined;
  videoPermission: PermissionState | undefined;
  setAudioPermission: (permission: PermissionState) => void;
  setVideoPermission: (permission: PermissionState) => void;
  hasMicrophone: boolean | undefined;
  hasCamera: boolean | undefined;
  setHasMicrophone: (hasMicrophone: boolean) => void;
  setHasCamera: (hasCamera: boolean) => void;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
};

export const usePermissionsState = create<PermissionsState>((set, get) => ({
  audioPermission: undefined,
  videoPermission: undefined,
  setAudioPermission: (audioPermission) => set({ audioPermission }),
  setVideoPermission: (videoPermission) => set({ videoPermission }),
  hasMicrophone: undefined,
  hasCamera: undefined,
  setHasMicrophone: (hasMicrophone) => set({ hasMicrophone }),
  setHasCamera: (hasCamera) => set({ hasCamera }),
  isLoading: true,
  setIsLoading: (isLoading) => set({ isLoading }),
}));

export const useGatheringPermissions = () => {
  const {
    isLoading,
    audioPermission,
    videoPermission,
    hasMicrophone,
    hasCamera,
    setAudioPermission,
    setVideoPermission,
    setHasMicrophone,
    setHasCamera,
    setIsLoading,
  } = usePermissionsState();

  const handleCheckPermissions = useCallback(
    async (audioStatus: PermissionStatus, videoStatus: PermissionStatus) => {
      setHasMicrophone(
        DetectRTC.hasMicrophone && audioStatus.state === 'granted',
      );
      setHasCamera(DetectRTC.hasWebcam && videoStatus.state === 'granted');
    },
    [setHasMicrophone, setHasCamera],
  );

  const grantPermission = async (
    video: boolean,
    audio = DetectRTC.hasMicrophone,
  ) => {
    let constraints: MediaStreamConstraints = {
      video,
      audio,
    };

    try {
      await navigator.mediaDevices
        .getUserMedia(constraints)
        .then((stream) => stream.getTracks().forEach((track) => track.stop()));
      return checkPermissions();
    } catch (error) {
      return { audioStatus: 'denied', videoStatus: 'denied' };
    }
  };

  const checkPermissions = useCallback(async () => {
    const audioStatus = await navigator.permissions.query({
      name: 'microphone' as PermissionName,
    });
    const videoStatus = await navigator.permissions.query({
      name: 'camera' as PermissionName,
    });

    audioStatus.onchange = () => {
      setAudioPermission(audioStatus.state);
      handleCheckPermissions(audioStatus, videoStatus);
    };

    videoStatus.onchange = () => {
      setVideoPermission(videoStatus.state);
      handleCheckPermissions(audioStatus, videoStatus);
    };

    setAudioPermission(audioStatus.state);
    setVideoPermission(videoStatus.state);

    DetectRTC.load(() => {
      setIsLoading(false);
      handleCheckPermissions(audioStatus, videoStatus);
    });

    return {
      audioStatus: audioStatus.state,
      videoStatus: videoStatus.state,
    };
  }, [
    setAudioPermission,
    setVideoPermission,
    setIsLoading,
    handleCheckPermissions,
  ]);

  useEffect(() => {
    checkPermissions();
  }, [checkPermissions]);

  return {
    isLoading,
    hasMicrophone,
    hasCamera,
    audioPermission,
    videoPermission,
    grantPermission,
  };
};
